/**
 * Team chat hooks — react-query polling stands in for realtime (same pattern
 * as reminders): the open conversation polls every 4s, the conversation list
 * every 10s, and the global unread summary every 15s.
 */
import { useEffect } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  createGroup,
  deleteGroup,
  deleteMessage,
  editMessage,
  fetchReceipts,
  getUnreadSummary,
  listMessages,
  listPins,
  listTeamConversations,
  markConversationRead,
  openDm,
  pinMessage,
  reorderConversations,
  searchTeamChat,
  sendMessage,
  toggleReaction,
  unpinMessage,
  updateGroup,
  type MessagesPage,
  type TeamChatAttachment,
  type TeamChatMessage,
  type TeamConversation,
} from '../lib/teamChatApi';
import { useModules } from './useModules';

/**
 * Global unread badge for the app sidebar. Mounted in the Sidebar, so it polls
 * on every page — keep the interval generous.
 */
export function useTeamChatUnread() {
  // No polling while the module is off (every caller — sidebar badge, chat
  // widget, reminder bar, mobile ports — goes through here).
  const enabled = useModules().isOn('teamChat');
  return useQuery({
    queryKey: ['team-chat', 'unread'],
    enabled,
    queryFn: getUnreadSummary,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
  });
}

/** Conversation list — mounted only on /team-chat*. */
export function useTeamChatConversations() {
  return useQuery({
    queryKey: ['team-chat', 'conversations'],
    queryFn: listTeamConversations,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 10_000,
  });
}

/**
 * Message pages for the open conversation, newest page first. maxPages: 3 caps
 * what each 4s poll refetches at 3×50 messages; scrolling further up refetches
 * the trimmed pages via fetchNextPage (react-query v5 behaviour).
 */
export function useTeamChatMessages(conversationId: string | null) {
  return useInfiniteQuery({
    queryKey: ['team-chat', 'messages', conversationId],
    queryFn: ({ pageParam }) => listMessages(conversationId!, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page: MessagesPage) => page.nextCursor,
    maxPages: 3,
    enabled: conversationId !== null,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 4_000,
  });
}

/**
 * Read watermarks for the open conversation, on the same 4s beat as the
 * messages — the ticks turning blue shouldn't visibly lag the message arriving.
 */
export function useConversationReceipts(conversationId: string | null) {
  return useQuery({
    queryKey: ['team-chat', 'receipts', conversationId],
    queryFn: () => fetchReceipts(conversationId!),
    enabled: conversationId !== null,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 4_000,
  });
}

/**
 * Message search. `q` is expected pre-debounced by the caller; anything shorter
 * than two characters is not sent at all (the server would answer empty). No
 * polling — a result list that reshuffled under the cursor would be hostile.
 */
export function useTeamChatSearch(q: string, conversationId?: string) {
  return useQuery({
    queryKey: ['team-chat', 'search', q, conversationId ?? null],
    queryFn: () => searchTeamChat(q, conversationId),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function useTeamChatPins(conversationId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['team-chat', 'pins', conversationId],
    queryFn: () => listPins(conversationId!),
    enabled: conversationId !== null && enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['team-chat'] });
}

export function useOpenDm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => openDm(userId),
    onSettled: () => invalidateAll(qc),
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; memberIds: string[] }) => createGroup(input),
    onSettled: () => invalidateAll(qc),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: { name?: string; addMemberIds?: string[]; removeMemberIds?: string[] };
    }) => updateGroup(id, patch),
    onSettled: () => invalidateAll(qc),
  });
}

/**
 * Persist a drag in the sidebar. Patches the cached list first: the list only
 * refetches every 10s, so without it the dragged row would visibly snap back.
 */
export function useReorderConversations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationIds: string[]) => reorderConversations(conversationIds),
    onMutate: (conversationIds) => {
      qc.setQueryData<TeamConversation[]>(['team-chat', 'conversations'], (data) =>
        data?.map((c) => {
          const index = conversationIds.indexOf(c.id);
          return index === -1 ? c : { ...c, sortOrder: index };
        }),
      );
    },
    onSettled: () => invalidateAll(qc),
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteGroup(id),
    onSettled: () => invalidateAll(qc),
  });
}

export function useSendMessage(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      body: string;
      attachments?: TeamChatAttachment[];
      replyToId?: string | null;
    }) => sendMessage(conversationId, input),
    // Optimistically append to the newest page so the message shows without
    // waiting for the next poll.
    onSuccess: (message) => {
      qc.setQueryData<InfiniteData<MessagesPage, string | null>>(
        ['team-chat', 'messages', conversationId],
        (data) => {
          if (!data || data.pages.length === 0) return data;
          const [first, ...rest] = data.pages;
          if (first.messages.some((m) => m.id === message.id)) return data;
          return {
            ...data,
            pages: [{ ...first, messages: [message, ...first.messages] }, ...rest],
          };
        },
      );
    },
    onSettled: () => invalidateAll(qc),
  });
}

/** Patch one message in place across all cached pages of its conversation. */
function replaceMessageInCache(qc: ReturnType<typeof useQueryClient>, message: TeamChatMessage) {
  qc.setQueryData<InfiniteData<MessagesPage, string | null>>(
    ['team-chat', 'messages', message.conversationId],
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          messages: page.messages.map((m) => (m.id === message.id ? message : m)),
        })),
      },
  );
}

export function useEditMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => editMessage(id, body),
    onSuccess: (message) => replaceMessageInCache(qc, message),
    onSettled: () => invalidateAll(qc),
  });
}

export function useDeleteMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMessage(id),
    onSettled: () => invalidateAll(qc),
  });
}

export function useTogglePin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) =>
      pinned ? unpinMessage(id) : pinMessage(id),
    onSuccess: (message) => replaceMessageInCache(qc, message),
    onSettled: () => invalidateAll(qc),
  });
}

export function useToggleReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, emoji }: { id: string; emoji: string }) => toggleReaction(id, emoji),
    onSuccess: (message) => replaceMessageInCache(qc, message),
    onSettled: () => invalidateAll(qc),
  });
}

/**
 * Clear the unread state while a conversation is open: mark read on window
 * focus and whenever a new latest message arrives (`latestMessageId`).
 *
 * Deliberately a no-op while `latestMessageId` is null. The effect runs on
 * mount, before the first page of messages has loaded — marking read then would
 * claim messages that were never on screen, which is visible to the *sender* as
 * ticks going blue against a reader who saw nothing.
 */
export function useMarkConversationRead(
  conversationId: string | null,
  latestMessageId: string | null,
) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!conversationId || !latestMessageId) return;
    const mark = () => {
      void markConversationRead(conversationId, latestMessageId)
        .then(() => {
          void qc.invalidateQueries({ queryKey: ['team-chat', 'unread'] });
          void qc.invalidateQueries({ queryKey: ['team-chat', 'conversations'] });
        })
        .catch(() => undefined);
    };
    mark();
    const onFocus = () => {
      if (document.visibilityState === 'visible') mark();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [conversationId, latestMessageId, qc]);
}
